class PredictiveSearch extends HTMLElement {
  static instanceCount = 0;
  constructor() {
    super();
    this.cachedResults = {};
    this.requestId = 0;
    this.instanceId = ++PredictiveSearch.instanceCount;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener("input", () => this.invalidateResults());
    this.select?.addEventListener("input", () => this.invalidateResults());

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "Escape") {
      this.invalidateResults();
    }
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  invalidateResults() {
    this.requestId += 1;
    this.requestController?.abort();
    this.removeAttribute("results");
    this.removeAttribute("loading");
    this.close();
  }

  exactQueries(searchTerm) {
    if (searchTerm.length < 2 || searchTerm.length > 128 || /[:"()]/.test(searchTerm)) return [];
    const quote = (value) => '"' + value.replaceAll("\\", "\\\\").replaceAll('"', '\\"') + '"';
    return [
      `variants.sku:${quote(searchTerm)}`,
      searchTerm.split(/\s+/u).map((word) => `title:${quote(word)}`).join(" AND "),
    ];
  }

  async fetchResults(query, signal, productsOnly = false) {
    const params = new URLSearchParams({
      q: query,
      "resources[limit_scope]": this.dataset.limitScope || "each",
      "resources[limit]": this.dataset.limit || "10",
      section_id: "predictive-search",
    });
    if (productsOnly) params.set("resources[type]", "product");
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    const timeout = setTimeout(abort, productsOnly ? 1800 : 8000);
    try {
      if (signal.aborted) controller.abort();
      const response = await fetch(`${routes.predictive_search_url}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const section = doc.querySelector("#shopify-section-predictive-search");
      if (!section) throw new Error("Search section missing");
      return section;
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
    }
  }

  mergeResults(base, exact, searchTerm) {
    const selector = "#predictive-search-results-item-product-list";
    const normalList = base.querySelector(selector);
    const normalize = (text) => text.normalize("NFKC").trim().toLocaleLowerCase();
    const tokens = normalize(searchTerm).split(/\s+/u);
    const rows = [];
    const seen = new Set();
    const limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 10));
    let sourceList = normalList;
    [exact[0], exact[1], base].forEach((section, group) => {
      const list = section?.querySelector(selector);
      if (!list) return;
      sourceList ||= list;
      const candidates = Array.from(list.children);
      if (group === 1) {
        // A model at the beginning of a product name is more specific than a
        // compatibility mention in an accessory name. Keep SKU priority above it.
        const startsWithQuery = (row) => {
          const words = (text) => normalize(text).split(/[^\p{L}\p{N}-]+/u).filter(Boolean);
          const title = words(row.dataset.productTitle || "");
          const vendor = words(row.dataset.productVendor || "");
          const query = words(searchTerm);
          const starts = (value, prefix) => prefix.length > 0 && prefix.every((word, i) => value[i] === word);
          const withoutVendor = starts(title, vendor) ? title.slice(vendor.length) : title;
          return starts(title, query) || starts(withoutVendor, query);
        };
        candidates.sort((a, b) => Number(startsWithQuery(b)) - Number(startsWithQuery(a)));
      }
      for (const row of candidates) {
        if (group === 0 && normalize(row.dataset.productSku || "") !== normalize(searchTerm)) continue;
        if (group === 1) {
          const titleWords = normalize(row.dataset.productTitle || "").split(/[^\p{L}\p{N}-]+/u);
          if (!tokens.every((token) => titleWords.includes(token))) continue;
        }
        const key = row.dataset.productId || new URL(row.querySelector("a").href, location.origin).pathname;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push(row.cloneNode(true));
      }
    });
    if (rows.length) {
      let target = normalList;
      if (!target) {
        const group = sourceList.parentElement.cloneNode(true);
        target = group.querySelector(selector);
        const wrapper = base.querySelector(".predictive-search-results-group-wrapper");
        if (!wrapper?.children[1]) return base.innerHTML;
        wrapper.children[1].prepend(group);
      }
      target.replaceChildren(...rows.slice(0, limit));
      base.querySelector("#predictive-search-results-list")?.classList.remove("predictive-search-results-list-empty");
      base.querySelectorAll(".predictive-search-loading-state").forEach((node) => node.remove());
      const count = base.querySelector("[data-predictive-search-live-region-count-value]");
      if (count?.dataset.resultsTemplate) {
        const otherCount = Number(count.dataset.otherCount) || 0;
        count.textContent = count.dataset.resultsTemplate.replace("__COUNT__", String(target.children.length + otherCount));
      }
    }
    return base.innerHTML;
  }

  async getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey = JSON.stringify([searchTerm.toLowerCase(), filterType, filterTypeQuery]);
    this.requestController?.abort();
    const requestId = ++this.requestId;
    const controller = new AbortController();
    this.requestController = controller;
    this.setLiveRegionLoadingState();
    const current = () => requestId === this.requestId && this.getQuery() === searchTerm &&
      this.getSearchFilterTypeQuery() === filterTypeQuery && this.contains(document.activeElement);
    if (this.cachedResults[queryKey]) {
      if (current()) this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    const scope = (query) => filterTypeQuery ? `${filterType}:"${filterTypeQuery.replaceAll('"', '\\"')}" AND (${query})` : query;
    const exactQueries = this.exactQueries(searchTerm);
    const results = await Promise.allSettled([
      this.fetchResults(scope(searchTerm), controller.signal),
      ...exactQueries.map((query) => this.fetchResults(scope(query), controller.signal, true)),
    ]);
    if (!current()) return;
    const base = results[0].status === "fulfilled" ? results[0].value : null;
    if (!base) {
      this.removeAttribute("loading");
      this.close();
      return;
    }
    const exact = results.slice(1).map((result) => result.status === "fulfilled" ? result.value : null);
    const markup = this.mergeResults(base, exact, searchTerm);
    if (results.every((result) => result.status === "fulfilled")) {
      if (Object.keys(this.cachedResults).length >= 30) delete this.cachedResults[Object.keys(this.cachedResults)[0]];
      this.cachedResults[queryKey] = markup;
    }
    this.renderSearchResults(markup);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.input.setAttribute("aria-activedescendant", "");
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.querySelectorAll('[role="option"]').forEach((node, index) => {
      node.id = `predictive-search-${this.instanceId}-option-${index}`;
      node.setAttribute("aria-selected", "false");
    });
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    const top = this.querySelector(".predictive-search").getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = `calc(100dvh - ${top}px - 30px)`;

    if (!this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
